import { cn } from '@/lib/utils';
import { Check } from '@mynaui/icons-react';
import { FunctionComponent, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import PortalTooltip from './PortalTooltip';

const formatCoords = (coords: any): any => {
  if (typeof coords === 'number') {
    return Number(coords).toFixed(3);
  } else if (Array.isArray(coords)) {
    return coords.map(formatCoords);
  } else if (coords && typeof coords === 'object') {
    // Preserve object structures as-is, but recurse into their values
    const formatted: any = {};
    for (const key in coords) {
      if (Object.prototype.hasOwnProperty.call(coords, key)) {
        formatted[key] = formatCoords(coords[key]);
      }
    }
    return formatted;
  }
  return coords;
};

interface iCoordsProps {
  coords: any;
}
export const Coords: FunctionComponent<iCoordsProps> = ({ coords }) => {
  const [isCopied, setIsCopied] = useState(false);
  const id = useId();
  const copiedTimeout = useRef<any | null>(null);
  const value = useMemo(() => {
    if (Array.isArray(coords) && coords.every((v) => typeof v === 'number')) {
      return `(${coords.map((v) => v.toFixed(3)).join(', ')})`;
    }
    return JSON.stringify(formatCoords(coords), null, 2);
  }, [coords]);

  const _handleCopyCoordinates = () => {
    navigator.clipboard.writeText(value);
    toast.success('Coordinates copied to clipboard');
    setIsCopied(true);
    if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };
  const isOneDimensional = Array.isArray(coords) && coords.every((v) => typeof v === 'number');
  return (
    <>
      <pre
        data-tooltip-id={id}
        data-tooltip-delay-show={320}
        onClick={_handleCopyCoordinates}
        className='text-sm whitespace-pre-wrap cursor-copy font-mono font-medium relative bg-slate-100 dark:bg-stone-900/70 rounded-sm px-1.5 py-0.75 w-fit'>
        {value}
        <span
          className={cn(
            'absolute inset-0 rounded-sm bg-slate-100 dark:bg-stone-900/70 backdrop-blur-sm flex items-center justify-center text-xs font-medium pointer-events-none transition-all',
            isCopied ? 'opacity-100' : 'opacity-0',
          )}>
          <span
            className={cn(
              'transition-all font-sans font-medium flex items-center gap-1',
              isCopied ? 'scale-100' : 'scale-70',
            )}>
            <Check className='size-4 stroke-2' />
            Copied to clipboard
          </span>
        </span>
      </pre>
      <PortalTooltip id={id}>
        <span className='text-sm font-medium'>
          Coordinates <span className='text-xs font-medium opacity-70'>(Click to copy)</span>
        </span>
        <div className='font-mono leading-tight mt-0.5'>
          {isOneDimensional ? (
            <>
              <div>X: {coords[0].toFixed(6)}</div>
              <div>Y: {coords[1].toFixed(6)}</div>
              <div>Z: {coords[2].toFixed(6)}</div>
            </>
          ) : (
            <pre>{value}</pre>
          )}
        </div>
      </PortalTooltip>
    </>
  );
};
